/**
 * PrescriptionTemplatesManager - Tela de Gerenciamento de Templates
 * "Minhas Receitas Modelo" - Criar, Editar, Duplicar, Excluir, Favoritar
 */

import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  FileText, 
  Star, 
  Edit, 
  Copy, 
  Trash2, 
  Pill,
  Clock,
  Sparkles,
  MoreVertical,
  AlertTriangle,
  Printer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PrescriptionTemplateModal from './PrescriptionTemplateModal';
import GenerateFromTemplateModal from './GenerateFromTemplateModal';

export default function PrescriptionTemplatesManager({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [generateTemplate, setGenerateTemplate] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  // Buscar com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) {
        fetchTemplates();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/prescription-templates/', {
        params: { search: searchQuery || undefined }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (template) => {
    try {
      await axiosInstance.post(`/prescription-templates/${template.id}/toggle-favorite`);
      fetchTemplates();
      toast.success(template.is_favorite ? 'Removido dos favoritos' : 'Adicionado aos favoritos');
    } catch (error) {
      toast.error('Erro ao atualizar favorito');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axiosInstance.post(`/prescription-templates/${template.id}/duplicate`);
      fetchTemplates();
      toast.success('Template duplicado com sucesso!');
    } catch (error) {
      toast.error('Erro ao duplicar template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${template.template_name}"?`)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/prescription-templates/${template.id}`);
      fetchTemplates();
      toast.success('Template excluído com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowTemplateModal(true);
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowTemplateModal(true);
  };

  const seedDefaultTemplates = async () => {
    try {
      const response = await axiosInstance.post('/prescription-templates/seed-defaults');
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao criar templates de exemplo');
    }
  };

  return (
    <>
      <Dialog open={open && !showTemplateModal && !showGenerateModal} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5 text-primary" />
              Minhas Receitas Modelo
            </DialogTitle>
            <DialogDescription>
              Gerencie seus templates de receitas para reutilização rápida durante atendimentos.
            </DialogDescription>
          </DialogHeader>

          {/* Barra de ferramentas */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar templates..."
                className="pl-10"
              />
            </div>
            <Button onClick={handleCreateNew} className="bg-primary" data-testid="new-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Receita Modelo
            </Button>
            <Button 
              onClick={() => { setGenerateTemplate(null); setShowGenerateModal(true); }} 
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="generate-from-template-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Gerar Receita Rapida
            </Button>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-500">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : templates.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Nenhum template encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {searchQuery 
                  ? 'Nenhum template corresponde à sua busca.'
                  : 'Crie seu primeiro template de receita para agilizar suas prescrições.'}
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={seedDefaultTemplates}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Criar Exemplos
                </Button>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Template
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Aviso */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <strong>Dica:</strong> Templates são modelos para agilizar prescrições. 
                Editar um template não altera receitas já criadas com ele.
              </div>

              {/* Estatísticas */}
              <div className="flex gap-4 text-sm text-gray-500">
                <span>{templates.length} template(s)</span>
                <span>•</span>
                <span>{templates.filter(t => t.is_favorite).length} favorito(s)</span>
              </div>

              {/* Lista de Templates */}
              <div className="grid gap-3">
                {templates.map(template => (
                  <div 
                    key={template.id}
                    className={`p-4 border rounded-xl transition-all hover:shadow-md ${
                      template.is_favorite 
                        ? 'border-yellow-300 bg-yellow-50/50' 
                        : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`template-item-${template.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleFavorite(template)}
                            className="flex-shrink-0"
                          >
                            <Star 
                              className={`w-5 h-5 transition-colors ${
                                template.is_favorite 
                                  ? 'text-yellow-500 fill-yellow-500' 
                                  : 'text-gray-300 hover:text-yellow-400'
                              }`}
                            />
                          </button>
                          <h4 className="font-semibold text-gray-900 truncate">
                            {template.template_name}
                          </h4>
                        </div>
                        
                        {template.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {template.description}
                          </p>
                        )}
                        
                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            <Pill className="w-3 h-3 mr-1" />
                            {template.medications?.length || 0} medicamento(s)
                          </Badge>
                          
                          {template.specialty && (
                            <Badge variant="outline" className="text-xs">
                              {template.specialty}
                            </Badge>
                          )}
                          
                          {template.disease_tags?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-blue-50">
                              {tag}
                            </Badge>
                          ))}
                          
                          {template.disease_tags?.length > 2 && (
                            <span className="text-xs text-gray-400">
                              +{template.disease_tags.length - 2}
                            </span>
                          )}
                        </div>
                        
                        {/* Meta info */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          {template.use_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Usado {template.use_count}x
                            </span>
                          )}
                          <span>
                            Criado em {new Date(template.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>

                      {/* Menu de ações */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setGenerateTemplate(template); setShowGenerateModal(true); }}>
                            <Printer className="w-4 h-4 mr-2" />
                            Gerar Receita
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEdit(template)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleFavorite(template)}>
                            <Star className={`w-4 h-4 mr-2 ${template.is_favorite ? 'fill-yellow-500' : ''}`} />
                            {template.is_favorite ? 'Remover Favorito' : 'Favoritar'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(template)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de criar/editar template */}
      <PrescriptionTemplateModal
        open={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
        onSuccess={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
          fetchTemplates();
        }}
      />

      {/* Modal de gerar receita a partir do template */}
      <GenerateFromTemplateModal
        open={showGenerateModal}
        onClose={() => {
          setShowGenerateModal(false);
          setGenerateTemplate(null);
        }}
        template={generateTemplate}
      />
    </>
  );
}
